import { ChangeDetectionStrategy, Component, DestroyRef, inject, input } from '@angular/core';
import { TranslateModule } from '@ngx-translate/core';
import { ReactiveFormsModule } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { MatSortModule } from '@angular/material/sort';
import { MatTableModule } from '@angular/material/table';
import { FindingsDataSource } from './findings-data-source';
import { FindingsListComponent } from 'component/details/requirement-intake/requirements-detail/findings/list/findings-list.component';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { combineLatest, map } from 'rxjs';
import { CardHeaderComponent } from 'common/card/header/card-header.component';
import { MatDialog } from '@angular/material/dialog';
import { AddFindingDialogComponent } from 'component/details/requirement-intake/requirements-detail/findings/add/add-finding-dialog.component';
import { RequirementIntake } from 'api';
import { IconDirective } from 'common/icon/icon.directive';
import { ButtonDirective } from 'common/button/button.directive';

@Component({
  selector: 'comp-ev-findings',
  templateUrl: 'findings.component.html',
  styleUrls: ['findings.component.scss'],
  imports: [
    TranslateModule,
    ReactiveFormsModule,
    MatSortModule,
    MatTableModule,
    FindingsListComponent,
    CardHeaderComponent,
    IconDirective,
    ButtonDirective,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FindingsComponent {
  private readonly route = inject(ActivatedRoute);
  private readonly destroyRef = inject(DestroyRef);
  protected dialog = inject(MatDialog);

  readonly requirement = input.required<RequirementIntake>();
  readonly findingsType = input<'default' | 'dtrack'>('default');
  readonly refreshTrigger = input(0);

  dataSource = new FindingsDataSource();

  constructor() {
    combineLatest([
      toObservable(this.requirement).pipe(map((req) => req.id)),
      toObservable(this.refreshTrigger),
      this.route.queryParamMap,
    ])
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(([requirementsApplicationId, _refreshTrigger, queryParamMap]) => {
        this.dataSource.reqApplicationId = requirementsApplicationId;
        this.dataSource.refreshFromQueryParams(queryParamMap);
      });
  }

  addFinding(): void {
    const fileAttachments = this.requirement().evidences.flatMap(
      (evidence) => evidence.fileAttachments,
    );
    const dialogRef = this.dialog.open(AddFindingDialogComponent, {
      data: {
        fileAttachments: fileAttachments,
      },
    });
    dialogRef.afterClosed().subscribe((newFinding) => {
      if (!newFinding?.id) {
        return;
      }

      this.dataSource.refreshFromQueryParams(this.route.snapshot.queryParamMap);
    });
  }
}
